import type { ReactNode } from 'react';
import { CTA_SECONDARY, humanizeTreasuryError } from './treasuryUi';

type SkeletonKind = 'kpi' | 'table' | 'dashboard' | 'wizard' | 'cash';

/** Skeletons TX9 — preferir a spinner fullscreen. */
export function TreasurySkeleton({
  kind = 'table',
  rows = 5,
  className = '',
}: {
  kind?: SkeletonKind;
  rows?: number;
  className?: string;
}) {
  if (kind === 'kpi') {
    return (
      <div
        className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-3 ${className}`}
        aria-busy="true"
        aria-label="Cargando indicadores"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg border bg-white p-4 animate-pulse">
            <div className="h-3 w-20 bg-slate-200 rounded" />
            <div className="mt-3 h-7 w-28 bg-slate-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (kind === 'dashboard') {
    return (
      <div className={`space-y-4 ${className}`} aria-busy="true" aria-label="Cargando posición">
        <TreasurySkeleton kind="kpi" />
        <div className="rounded-lg border bg-white h-40 animate-pulse" />
        <TreasurySkeleton kind="table" rows={6} />
      </div>
    );
  }

  if (kind === 'wizard') {
    return (
      <div className={`space-y-4 ${className}`} aria-busy="true" aria-label="Cargando asistente">
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 w-24 bg-slate-200 rounded-full animate-pulse" />
          ))}
        </div>
        <div className="rounded-lg border bg-white p-4 space-y-3 animate-pulse">
          <div className="h-3 w-32 bg-slate-200 rounded" />
          <div className="h-10 w-full bg-slate-100 rounded" />
          <div className="h-10 w-2/3 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  if (kind === 'cash') {
    return (
      <div className={`space-y-4 ${className}`} aria-busy="true" aria-label="Cargando Mi caja">
        <div className="h-20 rounded-lg border bg-white animate-pulse" />
        <TreasurySkeleton kind="kpi" />
        <TreasurySkeleton kind="table" rows={4} />
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border bg-white overflow-hidden ${className}`}
      aria-busy="true"
      aria-label="Cargando tabla"
    >
      <div className="border-b bg-slate-50 px-3 py-2 flex gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-3 w-16 bg-slate-200 rounded animate-pulse" />
        ))}
      </div>
      <div className="divide-y">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="px-3 py-3 flex gap-4 animate-pulse">
            <div className="h-4 w-12 bg-slate-100 rounded" />
            <div className="h-4 w-24 bg-slate-100 rounded" />
            <div className="h-4 flex-1 bg-slate-100 rounded" />
            <div className="h-4 w-16 bg-slate-100 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function TreasuryEmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div
      className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-6 py-10 text-center"
      role="status"
    >
      <p className="text-sm font-medium text-gray-900">{title}</p>
      {description && (
        <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">{description}</p>
      )}
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

export function TreasuryErrorPanel({
  title = 'No pudimos completar la operación',
  message,
  onRetry,
  retryLabel = 'Actualizar',
}: {
  title?: string;
  message?: unknown;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  const human = humanizeTreasuryError(message, 'Reintentá en unos segundos o pedí ayuda a un supervisor.');
  return (
    <div
      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
      role="alert"
    >
      <p className="font-medium">{title}</p>
      <p className="mt-1 text-red-800/90">{human}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className={`${CTA_SECONDARY} mt-3 border-red-200`}>
          {retryLabel}
        </button>
      )}
    </div>
  );
}

/** Modal de confirmación con impacto (monto / origen / destino). */
export function TreasuryConfirmDialog({
  open,
  title,
  body,
  impact,
  confirmLabel,
  danger,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  impact?: { label: string; value: string }[];
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="treasury-confirm-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 space-y-4">
        <h3 id="treasury-confirm-title" className="text-lg font-semibold text-gray-900">
          {title}
        </h3>
        <p className="text-sm text-gray-600">{body}</p>
        {impact && impact.length > 0 && (
          <dl className="rounded-md border bg-slate-50 px-3 py-2 text-sm space-y-1">
            {impact.map((row) => (
              <div key={row.label} className="flex justify-between gap-3">
                <dt className="text-slate-500">{row.label}</dt>
                <dd className="font-medium text-slate-900 text-right tabular-nums">{row.value}</dd>
              </div>
            ))}
          </dl>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" disabled={busy} onClick={onCancel} className={CTA_SECONDARY}>
            Volver
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className={danger ? 'px-3 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50' : 'px-3 py-2 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50'}
          >
            {busy ? 'Procesando…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
